import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../features/auth/stores/useAuthStore';
import { ensureGuestSession } from '../providers/GuestProvider';

export interface CartItemData {
    id: string;
    quantity: number;
    productId: string;
    variantId: string | null;
    lensVariantId?: string | null;
    prescriptionData: any | null;
    frameOnly?: boolean;
    product: {
        id: string;
        name: string; // From translation
        slug: string;
        price: number;
        stock: number;
        isPrescriptionReady?: boolean;
        isReadyReadingGlasses?: boolean;
        images: { url: string }[];
        sku: string;
        colors?: { images: { url: string }[] }[];
    };
    variant: {
        id: string;
        name: string;
        price: number | null;
        stock: number;
    } | null;
    lensPrice?: number | null;
    lensVariant?: {
        id: string;
        tier: string;
        refractiveIndex: number;
        price: number;
        lensProduct: {
            id: string;
            visionType: string;
            feature: string;
            translations: { locale: string; name: string }[];
        };
    } | null;
}

export interface CartData {
    id: string;
    items: CartItemData[];
    subtotal: number; // Computed on frontend
}

// 1. Fetch Cart
export const useCart = () => {
    return useQuery({
        queryKey: ['cart'],
        queryFn: async (): Promise<CartData> => {
            const { data } = await api.get('/cart');
            const cart = data?.data || { id: null, items: [] };

            // Compute subtotal on the fly
            let subtotal = 0;
            if (cart && Array.isArray(cart.items)) {
                cart.items.forEach((item: any) => {
                    const price = item.variant?.price !== null && item.variant?.price !== undefined
                        ? item.variant.price
                        : item.product?.price || 0;
                    const lensPrice = item.lensPrice || 0;
                    subtotal += (price + lensPrice) * (item.quantity || 1);
                });
            }
            return { ...cart, subtotal };
        },
        staleTime: 1000 * 60 * 5, // 5 minutes
    });
};

// 2. Add to Cart (Moved from useProduct.ts)
export const useAddToCart = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: { productId: string; variantId?: string; colorId?: string | null; lensVariantId?: string; quantity: number; prescriptionData?: any }) => {
            const { isAuthenticated, guestId } = useAuthStore.getState();
            if (!isAuthenticated && !guestId) {
                await ensureGuestSession();
            }
            const { data } = await api.post('/cart/items', payload);
            return data;
        },
        onMutate: () => {
            // Dispatch a global event immediately so Navbar can open the CartDrawer for instant feedback
            window.dispatchEvent(new CustomEvent('cart:open'));
        },
        onSuccess: (data) => {
            if (data?.guestId && !useAuthStore.getState().isAuthenticated) {
                useAuthStore.getState().setGuestId(data.guestId);
            }
            queryClient.invalidateQueries({ queryKey: ['cart'] });
        }
    });
};

// 3. Update Item Quantity
export const useUpdateCartItem = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ itemId, quantity }: { itemId: string; quantity: number }) => {
            const { data } = await api.patch(`/cart/items/${itemId}`, { quantity });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cart'] });
        }
    });
};

// 4. Remove Item
export const useRemoveCartItem = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (itemId: string) => {
            const { data } = await api.delete(`/cart/items/${itemId}`);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cart'] });
        }
    });
};

// 5. Clear Cart
export const useClearCart = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async () => {
            const { data } = await api.delete('/cart');
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cart'] });
        }
    });
};

export const hasUnresolvedRxItems = (cart: CartData | null | undefined) => {
    if (!cart?.items) return false;
    return cart.items.some(item => {
        // Exclude reading glasses or completely non-rx contact lenses
        if (item.prescriptionData?.entryMethod === 'READING_GLASSES' || item.prescriptionData?.entryMethod === 'CONTACT_LENS_NO_RX') {
            return false;
        }

        // For Contact Lenses With Rx, it's unresolved if we don't have prescription image or manual entry data yet
        if (item.prescriptionData?.entryMethod === 'CONTACT_LENS_RX') {
            const hasData = !!(item.prescriptionData.prescriptionImageUrl || item.prescriptionData.rightEye);
            return !hasData;
        }

        // Standard logic for regular frames
        return !!item.product?.isPrescriptionReady && !item.lensVariantId && !item.frameOnly;
    });
};

// 6. Apply Coupon
export const useApplyCoupon = () => {
    return useMutation({
        mutationFn: async (payload: { code: string; cartId: string }) => {
            const { data } = await api.post('/coupons/apply', payload);
            return data.data; // { valid, discountAmount, finalTotal, coupon: {} }
        }
    });
};

// 7. Upload Prescription Image/PDF
export const useUploadPrescription = () => {
    return useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append('file', file);
            
            const { data } = await api.post('/cart/upload-prescription', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            return data; // { success: true, url: string }
        }
    });
};

// 8. Update Item Lens/Prescription Data
export const useUpdateCartItemLens = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ itemId, payload }: { itemId: string; payload: { lensVariantId?: string; prescriptionData?: any; frameOnly?: boolean } }) => {
            const { data } = await api.patch(`/cart/items/${itemId}`, payload);
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cart'] });
        }
    });
};
